"use client";

import { Button, Divider, PasswordInput, TextInput } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import Link from "next/link";
import { useParams, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useTransition } from "react";

import {
  setCustomerInfo,
  signIn,
  verifyToken,
} from "@repo/dashboard-core/actions";
import {
  loginSchema,
  type LoginFormValues,
} from "@repo/dashboard-core/schemas";

export default function PasswordLoginPage() {
  const t = useTranslations("auth.passwordlogin");
  const tv = useTranslations("validation");
  const router = useRouter();
  const params = useParams<{ locale: string }>();
  const [isPending, startTransition] = useTransition();

  const form = useForm<LoginFormValues>({
    initialValues: { email: "", password: "" },
    validate: zodResolver(loginSchema((key) => tv(key))),
  });

  const handleSubmit = (values: LoginFormValues) => {
    startTransition(async () => {
      try {
        const result = await signIn(values);

        if (result.token) {
          const decoded = await verifyToken(result.token);
          if (decoded) {
            await setCustomerInfo(decoded);
            const firstName = (decoded.firstName as string) || "";
            if (firstName) {
              notifications.show({
                title: t("success-title"),
                message: t("success-message", { firstName }),
                color: "green",
              });
            }
          }
          router.replace(`/${params.locale}`);
        } else {
          const isNetworkError = result.message === "network-error";
          notifications.show({
            title: t("error-title"),
            message: t(
              isNetworkError ? "error-network" : "error-invalid-credentials",
            ),
            color: "red",
          });
        }
      } catch {
        notifications.show({
          title: t("error-title"),
          message: t("error-network"),
          color: "red",
        });
      }
    });
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-2xl font-bold tracking-tight text-text-primary">
          {t("title")}
        </h1>
        <p className="mt-2 text-base text-text-secondary">{t("subtitle")}</p>
      </div>

      <form className="space-y-5" onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          label={t("email")}
          placeholder="you@example.com"
          type="email"
          size="md"
          radius="lg"
          {...form.getInputProps("email")}
        />

        <PasswordInput
          label={t("password")}
          size="md"
          radius="lg"
          {...form.getInputProps("password")}
        />

        <div className="flex justify-end">
          <Link
            href={`/${params.locale}/recover`}
            className="text-base text-primary hover:underline"
          >
            {t("forgot-password")}
          </Link>
        </div>

        <Button
          type="submit"
          fullWidth
          size="md"
          radius="lg"
          loading={isPending}
          className="mt-2"
        >
          {t("submit")}
        </Button>
      </form>

      <Divider label={t("or")} labelPosition="center" />

      <div className="text-center">
        <Link
          href={`/${params.locale}/login`}
          className="text-base font-medium text-primary hover:underline"
        >
          {t("magic-link")}
        </Link>
      </div>
    </div>
  );
}
